import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import cardBack from "../../@shared/assets/back-of-card.png";

export default function DeckStack({
  deck,
  amtOfCardsToDraw = 0,
  onFinishDrawAnimation,
}) {
  const [activeDraws, setActiveDraws] = useState([
    false,
    false,
    false,
    false,
    false,
  ]);
  const [deckReady, setDeckReady] = useState(false);
  const hasDrawnThisTurn = useRef(false);

  const control0 = useAnimation();
  const control1 = useAnimation();
  const control2 = useAnimation();
  const control3 = useAnimation();
  const control4 = useAnimation();

  const controls = [control0, control1, control2, control3, control4];

  const triggerDrawAtIndex = (index) => {
    if (!controls[index]) return;

    setActiveDraws((prev) => {
      const copy = [...prev];
      copy[index] = true;
      return copy;
    });

    controls[index]
      .start({
        x: -100,
        y: 100,
        opacity: [1, 0.5, 0],
        transition: {
          x: { duration: 0.5, ease: "easeOut" },
          y: { duration: 0.5, ease: "easeOut" },
          opacity: { duration: 0.4 },
        },
      })
      .then(() => {
        setActiveDraws((prev) => {
          const copy = [...prev];
          copy[index] = false;
          return copy;
        });
      });
  };

  const triggerDrawSequence = (count) => {
    console.log(`🎯 Drawing card at index ${count}`);

    let i = 0;
    const interval = setInterval(() => {
      if (i >= count || i >= controls.length) {
        clearInterval(interval);
        if (onFinishDrawAnimation) onFinishDrawAnimation();
        return;
      }

      triggerDrawAtIndex(i);
      i++;
    }, 250);
  };

  useEffect(() => {
    if (deckReady && amtOfCardsToDraw > 0 && !hasDrawnThisTurn.current) {
      hasDrawnThisTurn.current = true;
      triggerDrawSequence(amtOfCardsToDraw);
    }

    if (amtOfCardsToDraw === 0) {
      hasDrawnThisTurn.current = false;
    }
  }, [deckReady, amtOfCardsToDraw]);

  useEffect(() => {
    setDeckReady(true); // ✅ sets deckReady after initial mount
  }, []);

  return (
    <Box
      sx={{
        ...styles.deckContainer,
        bottom: { xs: "34%", md: "45%" },
        right: { xs: "-10%", md: "-3%" },
      }}
    >
      <Box style={styles.cardContainer}>
        <div style={styles.deckText}>{deck?.length}</div>

        {controls.map((animation, index) => (
          <motion.div
            key={`animated-${index}`}
            animate={animation}
            initial={{ x: 0, y: 0, opacity: 0 }}
            style={{
              position: "absolute",
              top: "0%",
              left: "40%",
              transform: "translate(-50%, -0%)",
              zIndex: 200,
              opacity: activeDraws[index] ? 1 : 0,
              pointerEvents: "none",
            }}
          >
            <img
              src={cardBack}
              alt=""
              style={{
                width: "55%",
                height: "45%",
                objectFit: "contain",
                transform: "skew(-30deg, -3deg) rotate(2deg)",
              }}
            />
          </motion.div>
        ))}

        {deck?.slice(0, 10).map((card, index) => (
          <img
            key={index}
            alt=""
            src={cardBack}
            style={{
              ...styles.card,
              zIndex: 100 - index,
              top: window.innerWidth >= 900 ? index * 2 + 50 : index * 2 + 10,
              zIndex: 100 - index,
              left: window.innerWidth >= 900 ? "20%" : "35%",
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

const styles = {
  deckContainer: {
    position: "absolute",
    bottom: "45%",
    right: "-3%",
    width: "140px",
    height: "140px",
    zIndex: 100,
    margin: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardContainer: {
    width: "100%",
    height: "100%",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  deckText: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: 16,
    fontWeight: "bold",
    color: "white",
    backgroundColor: "#00000090",
    borderRadius: 8,
    padding: "2px 6px",
    zIndex: 111,
  },
  card: {
    width: "55%",
    height: "45%",
    objectFit: "contain",
    transform: "skew(-30deg, -3deg) rotate(2deg) translate(-50%, -50%)",
    position: "absolute",
    top: "0%",
    left: "0%",
  },
  animatedCard: {
    width: "55%",
    height: "45%",
    objectFit: "contain",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "skew(-30deg, -3deg) rotate(2deg) translate(-50%, -50%)",
  },
};
